/** One update's copy in the Updates panel. */
export type UpdateCopy = {
  readonly name: string
  /** What the row says while the update is needed. */
  readonly stale: string
  /** What the row and Update all say while the update runs. */
  readonly busy: string
  /** The row's button. */
  readonly action: string
  /** The name takes a plural verb: savings are, pricing is. */
  readonly plural?: boolean
}

/** Where the seller fixes something the panel can't fix itself. */
export type UpdateFix = { readonly path: string; readonly label: string }

/** An update key other than `all`, which Update all reserves. */
type UpdateKey<K extends string> = `all` extends K ? never : K

/**
 * What can be out of date on one Proposal kind, and how its Updates panel
 * names, orders and links each update. Each kind decides from its own reads
 * which updates are needed; the panel only shows and runs them.
 */
export type UpdatesSpec<K extends string> = {
  /**
   * Every update, in the order a fix runs, at least one. `all` is reserved
   * for Update all, so a kind with an update by that name (or with plain
   * `string` keys) doesn't compile.
   */
  readonly order: readonly [UpdateKey<K>, ...UpdateKey<K>[]]
  /**
   * What each update reads, directly or through another update, which has to
   * be current before it runs.
   */
  readonly dependencies: Readonly<Record<K, readonly K[]>>
  readonly copy: Readonly<Record<K, UpdateCopy>>
  /** The sections each update's row scrolls to, and the rail dots it badges. */
  readonly sections: Readonly<Record<K, readonly string[]>>
  /** Prefix for the panel's element ids and test ids, e.g. `solar`. */
  readonly idPrefix: string
}

/**
 * What a fix runs, in fix order: the update and what it reads, or every
 * update for Update all.
 */
export const updatesPlan = <K extends string>({
  spec,
  target,
}: {
  spec: Pick<UpdatesSpec<K>, `dependencies` | `order`>
  target: K | `all`
}): readonly K[] =>
  target === `all`
    ? spec.order
    : spec.order.filter(
        (key) => key === target || spec.dependencies[target].includes(key),
      )

/**
 * The unknown reads a fix would build on, its own included, in fix order: it
 * can't run until they're checked again.
 */
export const updatesBlockedBy = <K extends string>({
  spec,
  key,
  unknown,
}: {
  spec: Pick<UpdatesSpec<K>, `dependencies` | `order`>
  key: K | `all`
  unknown: ReadonlySet<K> | undefined
}): readonly K[] =>
  updatesPlan({ spec, target: key }).filter(
    (read) => unknown?.has(read) === true,
  )

/** What the panel says while reads it builds on are unknown. */
export const updatesUnknownNote = <K extends string>({
  spec,
  keys,
}: {
  spec: Pick<UpdatesSpec<K>, `copy`>
  keys: readonly K[]
}): string => {
  const names = keys.map((key) => spec.copy[key].name.toLowerCase())
  const list =
    names.length > 1
      ? `${names.slice(0, -1).join(`, `)} and ${names[names.length - 1]}`
      : names[0]
  const verb = keys.length === 1 && !spec.copy[keys[0]].plural ? `is` : `are`
  return `Couldn't check whether ${list} ${verb} current.`
}
